import Button from "@/components/bricks/button/Button";
import ChevronLeftIcon from "@/components/icons/ChevronLeftIcon";
import DocumentIcon from "@/components/icons/DocumentIcon";
import DocumentModal from "@/components/Modal/modals/DocumentModal";
import { periodeIsActive, stringDateToPP } from "@/util/date";
import { formatLessons } from "@/util/number";

import type { DecoratedPersonEfteruddannelse } from "./decoratePeriod";
import { getPeriodStatusBadgeVariant, PeriodBadge } from "./PeriodBadge";
import { PeriodLessonTypeSummaryCard } from "./PeriodLessonTypeSummaryCard";
import { ReportDocumentAccordions } from "./ReportDocumentAccordions";

interface EfteruddannelsePeriodDetailViewProps {
  userId: string;
  period: DecoratedPersonEfteruddannelse;
  allPeriods: DecoratedPersonEfteruddannelse[];
  harForegaaendePeriode: boolean;
  lektionerIndberettetForrigePeriode?: number | null;
  onSaved: () => void;
  onBack: () => void;
  onSelectCertificate: (documentId: string) => void;
  onSelectReduction: (reductionId: string) => void;
  onSelectLegalAid: (legalAidId: string) => void;
  onSelectAuthorship: (authorshipId: string) => void;
  onSelectTeaching: (teachingId: string) => void;
}

const SummaryField = ({ label, value }: { label: string; value: string }) => (
  <div className="col-xl-3 col-md-6">
    <div className="h-100 rounded border bg-light p-3">
      <div className="text-muted small mb-1">{label}</div>
      <div className="fw-semibold fs-6">{value}</div>
    </div>
  </div>
);

export const EfteruddannelsePeriodDetailView = ({
  userId,
  period,
  allPeriods,
  harForegaaendePeriode,
  lektionerIndberettetForrigePeriode,
  onSaved,
  onBack,
  onSelectCertificate,
  onSelectReduction,
  onSelectLegalAid,
  onSelectAuthorship,
  onSelectTeaching,
}: EfteruddannelsePeriodDetailViewProps) => {
  const periodLabel = `${stringDateToPP(period.periode.fraDato)} – ${stringDateToPP(period.periode.tilDatoInklusiv)}`;
  const isActive = periodeIsActive(
    new Date(period.periode.fraDato),
    new Date(period.periode.tilDatoInklusiv),
  );
  const hasErklaering = period.personEfteruddannelseIndberetning.some(
    (indberetning) =>
      indberetning.efteruddannelseIndberetningstype === "PERIODEUDLOEB",
  );

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center ">
        <Button color="secondary" transparent type="button" onClick={onBack}>
          <ChevronLeftIcon />
          Tilbage til efteruddannelse
        </Button>
      </div>

      <div className="c-card p-4 ">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <div className="text-muted small mb-1">Efteruddannelsesperiode</div>
            <h2 className="h3 mb-0">{periodLabel}</h2>
          </div>
          {(period.indberetningStatus || isActive || hasErklaering) && (
            <div className="d-flex flex-wrap align-items-center justify-content-end gap-3 flex-shrink-0">
              {period.indberetningStatus && (
                <PeriodBadge
                  label={period.indberetningStatus}
                  variant={getPeriodStatusBadgeVariant(
                    period.indberetningStatusId,
                    period.indberetningStatus,
                  )}
                />
              )}
              {isActive && (
                <PeriodBadge label="Indeværende periode" variant="active" />
              )}
              {hasErklaering && (
                <DocumentModal
                  getDocument={`person/${userId}/efteruddannelser/${period.id}/dokument`}
                  downloadLink={`person/${userId}/efteruddannelser/${period.id}/download`}
                  title={
                    <>
                      <DocumentIcon height={20} />
                      Efteruddannelse
                    </>
                  }
                  buttonContents={[
                    <DocumentIcon key="erklaering-icon" />,
                    <span key="erklaering-label">Vis erklæring</span>,
                  ]}
                  contentProps={{
                    className: "c-document c-document--efteruddannelse",
                    style: { whiteSpace: "pre-wrap" },
                  }}
                />
              )}
            </div>
          )}
        </div>

        <div className="row g-3 mt-2">
          <SummaryField
            label="Lektioner gennemført"
            value={formatLessons(period.lektionerGennemfoert)}
          />
          <SummaryField
            label="Overført fra forrige periode"
            value={formatLessons(period.lektionerOverfoert)}
          />
          <SummaryField
            label="Overført til næste periode"
            value={formatLessons(period.lektionerOverfoertTilNaeste)}
          />

          <SummaryField
            label="Frist"
            value={
              period.indberetningFrist
                ? stringDateToPP(period.indberetningFrist)
                : "-"
            }
          />
          <SummaryField
            label="Modtaget dato"
            value={
              period.registreringsDato
                ? stringDateToPP(period.registreringsDato)
                : "-"
            }
          />
          {period.signaturNavn ? (
            <SummaryField
              label="Indberetning signeret af"
              value={period.signaturNavn}
            />
          ) : null}
        </div>
      </div>
      <section className="mb-4 mt-4">
        <PeriodLessonTypeSummaryCard
          personId={userId}
          period={period}
          allPeriods={allPeriods}
          harForegaaendePeriode={harForegaaendePeriode}
          lektionerIndberettetForrigePeriode={
            lektionerIndberettetForrigePeriode
          }
          onSaved={onSaved}
        />
      </section>
      <section className="mb-4 mt-4">
        <h3 className="h5 mb-3">Indberettede lektionsdokumenter</h3>
        <ReportDocumentAccordions
          userId={userId}
          periodId={period.id}
          onSelectCertificate={onSelectCertificate}
          onSelectReduction={onSelectReduction}
          onSelectLegalAid={onSelectLegalAid}
          onSelectAuthorship={onSelectAuthorship}
          onSelectTeaching={onSelectTeaching}
        />
      </section>
    </div>
  );
};
